import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [currentStep, setCurrentStep] = useState('greeting');
  const [userData, setUserData] = useState({
    name: '',
    project: '',
    sector: '',
    phone: '',
    requirement: '',
  });
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initial greeting
      setTimeout(() => {
        addBotMessage("Hello! 👋 Welcome to URBANCIAL.");
        setTimeout(() => {
          addBotMessage("Please tell me your requirement so we can assist you better.\n\nMay I know your name?");
          setCurrentStep('name');
        }, 800);
      }, 400);
    }
  }, [isOpen]);

  const addBotMessage = (text) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'bot', text }]);
      setIsTyping(false);
    }, 400);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userInput = inputValue.trim();
    addUserMessage(userInput);
    setInputValue('');

    switch (currentStep) {
      case 'name':
        setUserData(prev => ({ ...prev, name: userInput }));
        setTimeout(() => {
          addBotMessage(`Nice to meet you, ${userInput}! 😊`);
          setTimeout(() => {
            addBotMessage("Which project are you interested in? (Or type 'Any' if not specific)");
            setCurrentStep('project');
          }, 800);
        }, 400);
        break;

      case 'project':
        setUserData(prev => ({ ...prev, project: userInput }));
        setTimeout(() => {
          addBotMessage("Which sector or location in Gurgaon are you looking at?\n\n(e.g., Golf Course Road, Cyber City, MG Road, Dwarka Expressway, or 'Any')");
          setCurrentStep('sector');
        }, 400);
        break;

      case 'sector':
        setUserData(prev => ({ ...prev, sector: userInput }));
        setTimeout(() => {
          addBotMessage("Could you please share your phone number so our team can reach out to you?");
          setCurrentStep('phone');
        }, 400);
        break;

      case 'phone':
        // Validate phone number
        const phoneRegex = /^[\d\s+-]{10,}$/;
        if (!phoneRegex.test(userInput)) {
          setTimeout(() => {
            addBotMessage("Please enter a valid phone number (at least 10 digits).");
          }, 400);
          return;
        }
        setUserData(prev => ({ ...prev, phone: userInput }));
        setTimeout(() => {
          addBotMessage("Any specific requirements? (Budget, property type, area size, etc.)\n\nType 'None' if no specific requirement.");
          setCurrentStep('requirement');
        }, 400);
        break;

      case 'requirement':
        const finalData = { 
          ...userData, 
          requirement: userInput,
          email: 'enquiry@chatbot.com' // Default email for chatbot enquiries
        };
        setUserData(finalData);
        
        // Format requirement for storage
        const formattedRequirement = `Project: ${finalData.project} | Sector: ${finalData.sector} | Requirement: ${userInput}`;
        
        // Submit to backend
        try {
          await axios.post(`${API}/chat-enquiries`, {
            name: finalData.name,
            phone: finalData.phone,
            email: finalData.email,
            requirement: formattedRequirement
          });
          
          setTimeout(() => {
            addBotMessage(`Thank you, ${finalData.name}! 🎉`);
            setTimeout(() => {
              addBotMessage("I've recorded your enquiry. Our property expert will contact you within 24 hours.\n\nHave a great day!");
              setCurrentStep('complete');
            }, 1000);
          }, 400);
        } catch (error) {
          setTimeout(() => {
            addBotMessage("Thank you for your interest! Our team will contact you soon.");
            setCurrentStep('complete');
          }, 400);
        }
        break;

      case 'complete':
        setTimeout(() => {
          addBotMessage("If you have any other questions, feel free to call us or visit our website. Thank you! 🙏");
        }, 400);
        break;

      default:
        break;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resetChat = () => {
    setMessages([]);
    setCurrentStep('greeting');
    setUserData({ name: '', project: '', sector: '', phone: '', requirement: '' });
    setIsOpen(false);
  };

  const getPlaceholder = () => {
    switch (currentStep) {
      case 'name': return 'Enter your name...';
      case 'project': return 'Enter project name or "Any"...';
      case 'sector': return 'Enter sector/location...';
      case 'phone': return 'Enter phone number...';
      case 'requirement': return 'Enter your requirements...';
      case 'complete': return 'Chat complete!';
      default: return 'Type your message...';
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#c9a84c] hover:bg-[#8a6a10] text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : ''}`}
        data-testid="chatbot-button"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[rgba(201,168,76,0.2)]"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#c9a84c] to-[#d4a828] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold">URBANCIAL</h3>
                <p className="text-white/80 text-xs flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full" />
                  Online
                </p>
              </div>
            </div>
            <button
              onClick={resetChat}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="chatbot-close"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#fdf9f0]">
            {messages.map((message, index) => (
              <div
                key={`${message.type}-${index}-${message.text.slice(0, 20)}`}
                className={`flex items-end gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.type === 'bot' && (
                  <div className="w-8 h-8 bg-[#c9a84c] rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] p-3 rounded-2xl ${
                    message.type === 'user'
                      ? 'bg-[#c9a84c] text-white rounded-br-md'
                      : 'bg-white text-[#2a1e00] rounded-bl-md shadow-sm border border-[rgba(201,168,76,0.2)]'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 bg-[#6a5a30] rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex items-end gap-2">
                <div className="w-8 h-8 bg-[#c9a84c] rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-[rgba(201,168,76,0.2)]">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#8a7a50] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#8a7a50] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#8a7a50] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-[rgba(201,168,76,0.2)]">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={getPlaceholder()}
                disabled={currentStep === 'complete' || currentStep === 'greeting' || isTyping}
                className="flex-1 rounded-full border-[rgba(201,168,76,0.2)] focus:border-[#c9a84c]"
                data-testid="chatbot-input"
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || currentStep === 'complete' || currentStep === 'greeting' || isTyping}
                className="w-10 h-10 rounded-full bg-[#c9a84c] hover:bg-[#8a6a10] p-0"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-[#8a7a50] text-center mt-2">
              Powered by URBANCIAL
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
